// CRM 타입 정의

// 리드 상태
export type LeadStatus = 'new' | 'contacted' | 'qualified' | 'converted' | 'lost';

// 리드 소스
export type LeadSource =
  | 'website'
  | 'referral'
  | 'advertisement'
  | 'cold_call'
  | 'trade_show'
  | 'social_media'
  | 'other';

// 영업기회 상태
export type OpportunityStatus = 'open' | 'quotation' | 'negotiation' | 'won' | 'lost';

// 영업기회 단계
export type OpportunityStage =
  | 'prospecting'
  | 'qualification'
  | 'proposal'
  | 'negotiation'
  | 'closed_won'
  | 'closed_lost';

// 활동 유형
export type ActivityType = 'call' | 'meeting' | 'email' | 'task' | 'note';

// 활동 상태
export type ActivityStatus = 'planned' | 'completed' | 'cancelled';

// 리드
export interface Lead {
  id: string;
  code: string;
  name: string;
  company?: string;
  email?: string;
  phone?: string;
  status: LeadStatus;
  source: LeadSource;
  assignedTo?: string;
  assignedToName?: string;
  expectedRevenue?: number;
  description?: string;
  nextContactDate?: string;
  createdAt: string;
  updatedAt: string;
}

// 영업기회
export interface Opportunity {
  id: string;
  code: string;
  name: string;
  customerId?: string;
  customerName?: string;
  leadId?: string;
  status: OpportunityStatus;
  stage: OpportunityStage;
  probability: number;
  expectedAmount: number;
  expectedCloseDate: string;
  assignedTo?: string;
  assignedToName?: string;
  source?: LeadSource;
  description?: string;
  createdAt: string;
  updatedAt: string;
}

// 고객
export interface Customer {
  id: string;
  code: string;
  name: string;
  type: 'individual' | 'company';
  email?: string;
  phone?: string;
  address?: string;
  industry?: string;
  website?: string;
  assignedTo?: string;
  assignedToName?: string;
  totalRevenue: number;
  lastContactDate?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// 활동
export interface Activity {
  id: string;
  type: ActivityType;
  subject: string;
  description?: string;
  status: ActivityStatus;
  relatedTo: 'lead' | 'opportunity' | 'customer';
  relatedId: string;
  relatedName: string;
  scheduledDate?: string;
  completedDate?: string;
  assignedTo?: string;
  assignedToName?: string;
  createdAt: string;
}

// 상태 레이블 및 색상
export const leadStatusConfig: Record<LeadStatus, { label: string; color: string }> = {
  new: { label: '신규', color: 'blue' },
  contacted: { label: '접촉', color: 'yellow' },
  qualified: { label: '검증', color: 'purple' },
  converted: { label: '전환', color: 'green' },
  lost: { label: '이탈', color: 'red' },
};

export const leadSourceConfig: Record<LeadSource, { label: string }> = {
  website: { label: '웹사이트' },
  referral: { label: '소개' },
  advertisement: { label: '광고' },
  cold_call: { label: '콜드콜' },
  trade_show: { label: '전시회' },
  social_media: { label: 'SNS' },
  other: { label: '기타' },
};

export const opportunityStatusConfig: Record<OpportunityStatus, { label: string; color: string }> = {
  open: { label: '진행중', color: 'blue' },
  quotation: { label: '견적', color: 'purple' },
  negotiation: { label: '협상', color: 'yellow' },
  won: { label: '성공', color: 'green' },
  lost: { label: '실패', color: 'red' },
};

export const opportunityStageConfig: Record<OpportunityStage, { label: string; probability: number }> = {
  prospecting: { label: '발굴', probability: 10 },
  qualification: { label: '검증', probability: 25 },
  proposal: { label: '제안', probability: 50 },
  negotiation: { label: '협상', probability: 75 },
  closed_won: { label: '성공', probability: 100 },
  closed_lost: { label: '실패', probability: 0 },
};

export const activityTypeConfig: Record<ActivityType, { label: string; color: string }> = {
  call: { label: '전화', color: 'blue' },
  meeting: { label: '미팅', color: 'green' },
  email: { label: '이메일', color: 'purple' },
  task: { label: '업무', color: 'yellow' },
  note: { label: '메모', color: 'gray' },
};

export const activityStatusConfig: Record<ActivityStatus, { label: string; color: string }> = {
  planned: { label: '예정', color: 'blue' },
  completed: { label: '완료', color: 'green' },
  cancelled: { label: '취소', color: 'red' },
};
